import React, { useState, useEffect } from 'react';
import { usersService } from '../services/users.service';
import { authService } from '../services/auth.service';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Layout/Header';
import { formatRoleLabel } from '../utils/roles';
import Combobox from '../components/Combobox';
import './Profile.css';

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile form
  const [profileData, setProfileData] = useState({
    fullName: '',
    department: '',
    branch: '',
    telNo: '',
  });

  // Password form
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Profile picture
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const [pictureError, setPictureError] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarStatus, setAvatarStatus] = useState<'idle' | 'preview' | 'uploaded' | 'failed'>('idle');
  const [avatarRetryCount, setAvatarRetryCount] = useState(0);

  const departments = ['IT', 'HR', 'ADMIN', 'FINANCE', 'SALES', 'SERVICE'];
  const branches = [
    'Ampara',
    'Anuradhapura',
    'BPC Wattala',
    'Galle',
    'Gampaha',
    'Hino Centre - Wattala',
    'Hyde Park Corner',
    'Kalutara',
    'Kandy',
    'Kurunegala',
    'Maharagama',
    'Matara',
    'Negombo',
    'Nugegoda',
    'Panchikawatte',
    'Ratmalana',
    'Ratnapura',
    'Toyota Plaza - Wattala'
  ];

  useEffect(() => {
    if (user) {
      setProfileData({
        fullName: user.fullName,
        department: user.department,
        branch: user.branch,
        telNo: user.telNo || '',
      });

      if (user.profilePicture) {
        setAvatarUrl(user.profilePicture);
        setAvatarStatus('uploaded');
      } else {
        setAvatarUrl('');
        setAvatarStatus('idle');
      }
    }
  }, [user]);

  useEffect(() => {
    if (avatarStatus !== 'failed' || avatarRetryCount >= 3) return;

    const timer = setTimeout(async () => {
      try {
        const latest = await authService.getProfile();
        if (latest.profilePicture) {
          setAvatarUrl(latest.profilePicture);
          setAvatarStatus('uploaded');
          setPictureError('');
          setAvatarRetryCount(0);
          updateUser(latest);
        } else {
          setAvatarRetryCount((count) => count + 1);
        }
      } catch {
        setAvatarRetryCount((count) => count + 1);
      }
    }, 1200);

    return () => clearTimeout(timer);
  }, [avatarStatus, avatarRetryCount, updateUser]);

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const updatedUser = await usersService.update(user.id, {
        fullName: profileData.fullName,
        department: profileData.department,
        branch: profileData.branch,
        telNo: profileData.telNo || undefined,
      });

      // Update context
      updateUser(updatedUser);

      setSuccess('Profile updated successfully!');
      setIsEditing(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    setIsChangingPassword(true);

    try {
      await usersService.update(user.id, {
        password: passwordData.newPassword,
      });

      setPasswordSuccess('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err: any) {
      setPasswordError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handlePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];
  if (!file) return;

  if (!file.type.startsWith('image/')) {
    setPictureError('Please select an image file');
    return;
  }

  if (file.size > 5 * 1024 * 1024) {
    setPictureError('Image size must be less than 5MB');
    return;
  }

  // Keep a base64 preview instead of an object URL so it survives after upload
  const base64Preview = await new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });

  setAvatarUrl(base64Preview);   // ← base64, never revoked
  setAvatarStatus('preview');
  setIsUploadingPicture(true);
  setPictureError('');

  try {
    const updatedUser = await usersService.uploadProfilePicture(file);
    updateUser(updatedUser);

    if (updatedUser.profilePicture) {
      // Don't switch away from the preview yet — let the <img onLoad> do it
      // so the user always sees something.
      setAvatarStatus('uploaded');
      // Only replace the URL once we know the CDN URL is usable
      const cdnImg = new Image();
      cdnImg.onload = () => {
        setAvatarUrl(updatedUser.profilePicture!);
      };
      cdnImg.onerror = () => {
        // CDN not ready — keep showing base64 preview and trigger retry loop
        setAvatarStatus('failed');
        setAvatarRetryCount(0);
        setPictureError('Profile picture not yet available; retrying…');
      };
      cdnImg.src = updatedUser.profilePicture;
    } else {
      setAvatarStatus('failed');
    }
  } catch (err: any) {
    setPictureError(err.response?.data?.message || 'Failed to upload picture');
    setAvatarStatus('failed');
    // Fall back to existing persisted picture or keep base64 preview
    setAvatarUrl(user?.profilePicture || base64Preview);
  } finally {
    setIsUploadingPicture(false);
    e.target.value = '';
    // ← removed URL.revokeObjectURL — nothing to revoke with base64
  }
};

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const displayProfilePicture = avatarUrl;

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="profile-page">
      <Header />
      <div className="profile-container">
        <h1>Profile Settings</h1>

        {/* Profile Card */}
        <div className="profile-section">
          <div className="profile-header-card">
            <div className="profile-avatar-wrapper">
              <div className={`profile-avatar-large ${displayProfilePicture ? 'has-photo' : ''}`}>
                {displayProfilePicture ? (
                  <img
                    src={displayProfilePicture}
                    alt={user.fullName}
                    onLoad={() => {
                      setAvatarStatus('uploaded');
                      setAvatarRetryCount(0);
                    }}
                    onError={() => {
                      if (avatarStatus === 'preview') {
                        // preview failed, fallback into persisted user avatar if present.
                        if (user.profilePicture) {
                          setAvatarUrl(user.profilePicture);
                          setAvatarStatus('uploaded');
                        } else {
                          setAvatarUrl('');
                          setAvatarStatus('idle');
                        }
                        return;
                      }

                      // uploaded URL might be eventually consistent; trigger auto-retry.
                      setAvatarStatus('failed');
                      setPictureError('Profile picture not yet available; retrying...');
                      setAvatarRetryCount((count) => count + 1);
                    }}
                  />
                ) : (
                  getInitials(user.fullName)
                )}
              </div>
              <label className="change-picture-btn">
                {isUploadingPicture ? 'Uploading...' : 'Change Photo'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePictureUpload}
                  disabled={isUploadingPicture}
                  hidden
                />
              </label>
              {pictureError && <span className="picture-error">{pictureError}</span>}
            </div>
            <div className="profile-basic-info">
              <h2>{user.fullName}</h2>
              <p className="profile-role-badge">{formatRoleLabel(user.role)}</p>
              <p className="profile-empno">Employee No: {user.empNo}</p>
            </div>
          </div>
        </div>

        {/* Edit Profile Section */}
        <div className="profile-section">
          <div className="section-header">
            <h3>Personal Information</h3>
            {!isEditing && (
              <button className="btn-edit" onClick={() => setIsEditing(true)}>
                Edit
              </button>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <form onSubmit={handleProfileSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={profileData.fullName}
                  onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
                  disabled={!isEditing}
                  required
                />
              </div>


              <div className="form-group">
                <label>Department</label>
                <Combobox
                  ariaLabel="Department"
                  value={profileData.department}
                  onChange={(v) => setProfileData({ ...profileData, department: v })}
                  disabled={!isEditing}
                  required
                  options={departments.map((dept) => ({ value: dept, label: dept }))}
                />
              </div>

              <div className="form-group">
                <label>Branch</label>
                <Combobox
                  ariaLabel="Branch"
                  value={profileData.branch}
                  onChange={(v) => setProfileData({ ...profileData, branch: v })}
                  disabled={!isEditing}
                  required
                  options={branches.map((branch) => ({ value: branch, label: branch }))}
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={profileData.telNo}
                  onChange={(e) => setProfileData({ ...profileData, telNo: e.target.value })}
                  disabled={!isEditing}
                  placeholder="077-1234567"
                />
              </div>

              <div className="form-group">
                <label>Employee No</label>
                <input
                  type="text"
                  value={user.empNo}
                  disabled
                  className="disabled-field"
                />
                <span className="field-note">Employee No cannot be changed</span>
              </div>
            </div>

            {isEditing && (
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setIsEditing(false);
                    setProfileData({
                      fullName: user.fullName,
                      department: user.department,
                      branch: user.branch,
                      telNo: user.telNo || '',
                    });
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </form>
        </div>

        {/* Change Password Section */}
        <div className="profile-section">
          <div className="section-header">
            <h3>Change Password</h3>
          </div>

          {passwordError && <div className="error-message">{passwordError}</div>}
          {passwordSuccess && <div className="success-message">{passwordSuccess}</div>}

          <form onSubmit={handlePasswordSubmit}>
            <div className="form-grid password-form">
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="Min 6 characters"
                  required
                />
              </div>

              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  required
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-save" disabled={isChangingPassword}>
                {isChangingPassword ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
